/**
 * TabRouter — renders the active tab content for ManagePlatform.
 *
 * Why a single `ctx` props bag instead of 70 individual props?
 *   • The state/setters/functions needed by the tabs are inherently coupled to
 *     ManagePlatform's page state. Splitting them into discrete props just
 *     means typing the same names twice (page → TabRouter → tab).
 *   • A single bag keeps the parent call short (`<TabRouter ctx={...} />`)
 *     and lets each branch destructure only what it needs.
 *   • All the actual logic still lives in the per-tab components and in the
 *     domain hooks (useAgentData / useStoreActions / useUserActions). This
 *     file is intentionally dumb — it just dispatches on `activeTab`.
 *
 * Extracted from ManagePlatform.js in iter-80.
 */
import { Building2, CheckCircle, Clock, FileText, Hash, ShoppingCart, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';

import MainTab from './tabs/MainTab';
import CategoryHubTab from './tabs/CategoryHubTab';
import UsersAccountsTab from './tabs/UsersAccountsTab';
import CloudGMUsersTab from './tabs/CloudGMUsersTab';
import StoreRequestsTab from './tabs/StoreRequestsTab';
import StoresTab from './tabs/StoresTab';
import ComingSoonTab from './tabs/ComingSoonTab';
import SupportTicketsTab from './tabs/SupportTicketsTab';
import AgentsSection from './tabs/AgentsSection';
import AgentTasksManager from './tabs/AgentTasksManager';
import AgentTaskSubmissionsView from './tabs/AgentTaskSubmissionsView';
import OrderTrackingTab from './tabs/OrderTrackingTab';
import MerchantProxyListingsManager from './tabs/MerchantProxyListingsManager';
import CustomerAdsManager from './tabs/CustomerAdsManager';
import ServicePlaceholderTab from './tabs/ServicePlaceholderTab';
import ServiceCategoriesTab from './tabs/ServiceCategoriesTab';

export default function TabRouter({ ctx }) {
  const a = ctx.activeTab;

  // Hub-level tabs (no inner card wrapper)
  if (a === 'main') {
    return (
      <MainTab
        language={ctx.language}
        isCloudGM={ctx.isCloudGM}
        users={ctx.users}
        pendingStores={ctx.pendingStores}
        approvedAgents={ctx.approvedAgents}
        agentRequests={ctx.agentRequests}
        setActiveTab={ctx.setActiveTab}
      />
    );
  }
  if (a === 'stores-management') {
    return <CategoryHubTab variant="stores-management" language={ctx.language} setActiveTab={ctx.setActiveTab} />;
  }
  if (a === 'agents-management') {
    return (
      <CategoryHubTab
        variant="agents-management"
        language={ctx.language}
        setActiveTab={ctx.setActiveTab}
        agentRequests={ctx.agentRequests}
        invitationCodes={ctx.invitationCodes}
        approvedAgents={ctx.approvedAgents}
      />
    );
  }
  if (a === 'listings-management') {
    return <CategoryHubTab variant="listings-management" language={ctx.language} setActiveTab={ctx.setActiveTab} />;
  }

  // Content sections (wrapped in <div>)
  return (
    <div>
      {(a === 'users' || a === 'accounts') && (
        <UsersAccountsTab
          language={ctx.language}
          platformInfo={ctx.platformInfo}
          users={ctx.users}
          loading={ctx.loading}
          userStatusFilter={ctx.userStatusFilter}
          setUserStatusFilter={ctx.setUserStatusFilter}
          setShowAddUserModal={ctx.setShowAddUserModal}
          statusBadge={ctx.statusBadge}
          openEditUserModal={ctx.openEditUserModal}
          setSelectedUser={ctx.setSelectedUser}
          setShowResetPasswordModal={ctx.setShowResetPasswordModal}
          sendResetLink={ctx.sendResetLink}
          unbanUserDirect={ctx.unbanUserDirect}
          processingUser={ctx.processingUser}
          toggleUserStatus={ctx.toggleUserStatus}
          toggleBanUser={ctx.toggleBanUser}
          setShowBanUserModal={ctx.setShowBanUserModal}
          setShowDeleteUserModal={ctx.setShowDeleteUserModal}
          onRefresh={ctx.onRefreshUsers}
        />
      )}

      {a === 'cloudgm-users' && <CloudGMUsersTab language={ctx.language} />}

      {a === 'store-requests' && (
        <StoreRequestsTab
          storeBusinessType={ctx.storeBusinessType}
          language={ctx.language}
          storeNotifications={ctx.storeNotifications}
          setStoreBusinessType={ctx.setStoreBusinessType}
          setStatusFilter={ctx.setStatusFilter}
          getBusinessTypeCounts={ctx.getBusinessTypeCounts}
          getFilteredStores={ctx.getFilteredStores}
          statusFilter={ctx.statusFilter}
          getStoreRequestCounts={ctx.getStoreRequestCounts}
          typeFilter={ctx.typeFilter}
          setTypeFilter={ctx.setTypeFilter}
          storeRequestDateFrom={ctx.storeRequestDateFrom}
          setStoreRequestDateFrom={ctx.setStoreRequestDateFrom}
          storeRequestDateTo={ctx.storeRequestDateTo}
          setStoreRequestDateTo={ctx.setStoreRequestDateTo}
          loading={ctx.loading}
          formatDate={ctx.formatDate}
          processingStore={ctx.processingStore}
          approveStore={ctx.approveStore}
          setSelectedStore={ctx.setSelectedStore}
          setShowStoreDetails={ctx.setShowStoreDetails}
          setShowModificationModal={ctx.setShowModificationModal}
          setShowRejectModal={ctx.setShowRejectModal}
        />
      )}

      {a === 'subscriptions' && <ComingSoonTab variant="subscriptions" setActiveTab={ctx.setActiveTab} language={ctx.language} />}

      {a === 'support-tickets' && (
        <SupportTicketsTab language={ctx.language} setActiveTab={ctx.setActiveTab} headers={ctx.getHeaders()} />
      )}

      {a === 'stores' && (
        <StoresTab
          activeTab={a}
          language={ctx.language}
          loading={ctx.loading}
          storeManageType={ctx.storeManageType}
          setStoreManageType={ctx.setStoreManageType}
          storeListFilter={ctx.storeListFilter}
          setStoreListFilter={ctx.setStoreListFilter}
          stores={ctx.stores}
          formatDate={ctx.formatDate}
          getStoreManageCounts={ctx.getStoreManageCounts}
          getStoresListFiltered={ctx.getStoresListFiltered}
          setSelectedStore={ctx.setSelectedStore}
          setShowStoreDetails={ctx.setShowStoreDetails}
        />
      )}

      {['agent-requests', 'agents', 'invitation-codes'].includes(a) && (
        <AgentsSection
          activeTab={a}
          language={ctx.language}
          loading={ctx.loading}
          token={ctx.token}
          agentRequests={ctx.agentRequests}
          getFilteredAgentRequests={ctx.getFilteredAgentRequests}
          agentRequestFilter={ctx.agentRequestFilter}
          setAgentRequestFilter={ctx.setAgentRequestFilter}
          approveAgentRequest={ctx.approveAgentRequest}
          processingAgent={ctx.processingAgent}
          setSelectedAgentRequest={ctx.setSelectedAgentRequest}
          setShowRejectAgentModal={ctx.setShowRejectAgentModal}
          approvedAgents={ctx.approvedAgents}
          revokeAgent={ctx.revokeAgent}
          blockAgent={ctx.blockAgent}
          unblockAgent={ctx.unblockAgent}
          archiveAgent={ctx.archiveAgent}
          invitationCodes={ctx.invitationCodes}
          setShowCreateCodeModal={ctx.setShowCreateCodeModal}
          copyCode={ctx.copyCode}
          deleteInvitationCode={ctx.deleteInvitationCode}
        />
      )}

      {a === 'agent-tasks' && <ComingSoonTab variant="agent-tasks-hub" setActiveTab={ctx.setActiveTab} language={ctx.language} />}
      {a === 'add-agent-task' && <AgentTasksManager language={ctx.language} mode="add" setActiveTab={ctx.setActiveTab} />}
      {a === 'follow-agent-tasks' && <AgentTaskSubmissionsView language={ctx.language} setActiveTab={ctx.setActiveTab} />}

      {a === 'order-tracking' && <OrderTrackingTab language={ctx.language} />}

      {a === 'market-follow' && <ComingSoonTab variant="market-follow-hub" setActiveTab={ctx.setActiveTab} language={ctx.language} />}

      {a === 'market-phone-numbers' && (
        <CustomerAdsManager
          category="phone_number"
          language={ctx.language}
          onBack={() => ctx.setActiveTab('market-follow')}
        />
      )}

      {a === 'market-cars' && (
        <CustomerAdsManager
          category="car"
          language={ctx.language}
          onBack={() => ctx.setActiveTab('market-follow')}
        />
      )}

      {a === 'market-car-plates' && (
        <CustomerAdsManager
          category="car_plate"
          language={ctx.language}
          onBack={() => ctx.setActiveTab('market-follow')}
        />
      )}

      {a === 'services' && (
        <div>
          <h2 className="text-xl font-bold text-white mb-5">طلبات الخدمات</h2>
          {ctx.loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <div key={i} className="h-24 bg-[#0a0a0a] rounded-xl animate-pulse" />)}
            </div>
          ) : ctx.serviceRequests.length === 0 ? (
            <div className="text-center py-16 text-[#808080]">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>لا يوجد طلبات خدمات</p>
            </div>
          ) : (
            <div className="space-y-3">
              {ctx.serviceRequests.map((req) => (
                <div key={req.id} className="p-4 bg-[#0a0a0a] rounded-xl border border-[#2a2a4e] hover:border-[#c9a227]/30 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <p className="font-bold text-white">{req.service_type}</p>
                        {ctx.statusBadge(req.status)}
                      </div>
                      <p className="text-sm text-[#808080]">{req.description || req.details}</p>
                      <p className="text-xs text-[#606060] mt-1">{req.customer_name} - {req.phone}</p>
                    </div>
                    {req.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => ctx.updateRequestStatus(req.id, 'in_progress')} className="bg-sky-600 hover:bg-sky-700 text-white">
                          <Clock className="h-3 w-3 me-1" />
                          بدء
                        </Button>
                        <Button size="sm" onClick={() => ctx.updateRequestStatus(req.id, 'completed')} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                          <CheckCircle className="h-3 w-3 me-1" />
                          إكمال
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {a === 'listings' && (
        <div>
          <h2 className="text-xl font-bold text-white mb-5">المنتجات والعقارات</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-[#0a0a0a] rounded-xl border border-[#2a2a4e] text-center">
              <Building2 className="h-6 w-6 text-[#c9a227] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{ctx.listings.real_estate.length}</p>
              <p className="text-xs text-[#808080]">عقارات</p>
            </div>
            <div className="p-4 bg-[#0a0a0a] rounded-xl border border-[#2a2a4e] text-center">
              <Hash className="h-6 w-6 text-[#c9a227] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{ctx.listings.numbers.length}</p>
              <p className="text-xs text-[#808080]">أرقام</p>
            </div>
            <div className="p-4 bg-[#0a0a0a] rounded-xl border border-[#2a2a4e] text-center">
              <Wrench className="h-6 w-6 text-[#c9a227] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{ctx.listings.spare_parts.length}</p>
              <p className="text-xs text-[#808080]">قطع غيار</p>
            </div>
            <div className="p-4 bg-[#0a0a0a] rounded-xl border border-[#2a2a4e] text-center">
              <ShoppingCart className="h-6 w-6 text-[#c9a227] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{ctx.listings.products.length}</p>
              <p className="text-xs text-[#808080]">منتجات</p>
            </div>
          </div>
        </div>
      )}

      {a === 'listings-ecommerce'   && <MerchantProxyListingsManager storeType="ecommerce"   titleAr="المتاجر الإلكترونية" titleEn="E-Commerce Stores" iconColor="emerald" testidPrefix="listings-ecommerce" />}
      {a === 'listings-real-estate' && <MerchantProxyListingsManager storeType="real_estate" titleAr="العقارات"            titleEn="Real Estate"        iconColor="blue"    testidPrefix="listings-real-estate" />}
      {a === 'listings-cars'        && <MerchantProxyListingsManager storeType="cars"        titleAr="السيارات"            titleEn="Cars"               iconColor="orange"  testidPrefix="listings-cars" />}
      {a === 'listings-numbers'     && <MerchantProxyListingsManager storeType="numbers"     titleAr="أرقام الموبايل والهواتف" titleEn="Phone & Mobile Numbers" iconColor="purple" testidPrefix="listings-numbers" />}
      {a === 'listings-car-plates'  && <MerchantProxyListingsManager storeType="car_plates"  titleAr="لوحات السيارات"      titleEn="Car Plates"         iconColor="amber"   testidPrefix="listings-car-plates" />}

      {a === 'service-company-setup' && <ServicePlaceholderTab tabKey="service-company-setup" />}
      {a === 'service-documents'     && <ServicePlaceholderTab tabKey="service-documents" />}
      {a === 'service-translation'   && <ServicePlaceholderTab tabKey="service-translation" />}
      {a === 'service-bill-payment'  && <ServicePlaceholderTab tabKey="service-bill-payment" />}
      {a === 'services-categories'   && <ServiceCategoriesTab navigate={ctx.navigate} />}
    </div>
  );
}
